import { useMemo } from 'react'

import { BidType, MarketProgram } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import type { AggregatedBidWrapper } from '@/features/bidding/utils/model/mapActivationGroupBidsToAggregatedBids'

/**
 * Maximum number of price steps allowed per PTU for Finland mFRR energy bids.
 */
const FINLAND_MFRR_ENERGY_MAX_PRICE_STEPS_PER_PTU = 20

/**
 * Hook to determine if the Finland mFRR energy pricing limitation warning should be shown.
 *
 * In Finland, mFRR energy bids (MFRR_UP and MFRR_DOWN) are limited to 20 price steps per PTU.
 * This hook checks if any activation group in the current bids exceeds this limit.
 *
 * @param bidsPerActivationGroup - Map of current bid wrappers by activation group UUID
 * @param groupParams - The grouped bid parameters (contains marketProgram and bidType)
 * @returns true if the Finland warning should be shown, false otherwise
 *
 * @example
 * ```tsx
 * const shouldShowFinlandWarning = useShouldShowFinlandWarning(bidsPerActivationGroup, groupParams)
 * ```
 */
export function useShouldShowFinlandWarning(
  bidsPerActivationGroup: Map<string, AggregatedBidWrapper>,
  groupParams: GroupedBidParams,
): boolean {
  const { selectedCountry } = useBiddingContext()

  return useMemo(() => {
    const isFinland = selectedCountry.toLowerCase() === 'fi'
    const isEnergyBid = groupParams.bidType === BidType.ENERGY
    const isMfrr =
      groupParams.marketProgram === MarketProgram.MFRR_UP || groupParams.marketProgram === MarketProgram.MFRR_DOWN

    if (!isFinland || !isEnergyBid || !isMfrr) {
      return false
    }

    // Check if any activation group exceeds the price steps limit
    return Array.from(bidsPerActivationGroup.values()).some((bidWrapper) =>
      bidWrapper.aggregatedBid.ptus.some(
        (ptu) => ptu.priceQuantities.length > FINLAND_MFRR_ENERGY_MAX_PRICE_STEPS_PER_PTU,
      ),
    )
  }, [bidsPerActivationGroup, selectedCountry, groupParams.marketProgram, groupParams.bidType])
}
